// src/TabsPage.jsx

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery,
  TextField,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBackOutlined,
  CheckCircleOutline,
  FilterAltOutlined,
  HourglassEmptyOutlined,
  Inventory2Outlined,
  PeopleAltOutlined,
  RefreshOutlined,
  SearchOutlined,
  SortOutlined,
  StorefrontOutlined,
} from "@mui/icons-material";
import VintedProductDetails from "./vinted_products_details";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
export default function TabsPage() {
  const [value, setValue] = useState(0);

  // === State pentru lista de produse și filtrare ===
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [brandFilter, setBrandFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // === State pentru lista de vânzători și filtrare ===
  const [sellers, setSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [sellersError, setSellersError] = useState(null);
  const [modelFilter, setModelFilter] = useState(""); // /// modificat: filtru după model
  const [sellerAgentFilter, setSellerAgentFilter] = useState("");

  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [sellerProducts, setSellerProducts] = useState([]);
  const [sellerProdLoading, setSellerProdLoading] = useState(false);
  const [sellerProdError, setSellerProdError] = useState(null);

  // === State pentru detail produs ===
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Pentru responsive
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const [sortDesc, setSortDesc] = useState(true);

  const surfaceSx = {
    bgcolor: "#ffffff",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
      bgcolor: "#fff",
    },
  };

  const tableContainerSx = (maxHeight) => ({
    ...surfaceSx,
    width: "100%",
    maxHeight,
    minHeight: 380,
    overflow: "auto",
    position: "relative",
  });

  const headerCellSx = {
    bgcolor: "#f8fafc",
    color: "#475569",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: 0,
    textTransform: "uppercase",
    borderBottom: "1px solid #e2e8f0",
    py: 1,
    "&.MuiTableCell-stickyHeader": {
      bgcolor: "#f8fafc",
    },
  };

  const bodyCellSx = {
    borderBottom: "1px solid #eef2f7",
    py: 1,
  };

  const tableRowSx = {
    cursor: "pointer",
    transition: "background-color 140ms ease, box-shadow 140ms ease",
    "&:hover": {
      bgcolor: alpha(theme.palette.primary.main, 0.04),
      boxShadow: "inset 3px 0 0 #2563eb",
    },
  };

  const mutedChipSx = {
    bgcolor: "#f1f5f9",
    color: "#64748b",
    border: "1px solid #e2e8f0",
    fontWeight: 600,
    fontSize: "0.68rem",
    height: 22,
    "& .MuiChip-label": { px: 0.75 },
  };

  const resetProductFilters = () => {
    setBrandFilter("");
    setCategoryFilter("");
    setAgentFilter("");
    setModelFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const renderAgentChip = (agent) =>
    agent ? (
      <Chip
        label={agent}
        size="small"
        sx={{
          bgcolor: agent === "Mirela" ? "#ecfdf5" : "#eff6ff",
          color: agent === "Mirela" ? "#047857" : "#1d4ed8",
          border: `1px solid ${agent === "Mirela" ? "#a7f3d0" : "#bfdbfe"}`,
          fontWeight: 700,
          fontSize: "0.68rem",
          height: 22,
          maxWidth: 78,
          "& .MuiChip-label": {
            px: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      />
    ) : (
      <Chip label="-" size="small" sx={mutedChipSx} />
    );

  const renderContactChips = (contacted, hasTel) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      {contacted === "Da" ? (
        <Chip
          label="Da"
          size="small"
          sx={{
            bgcolor: "#ecfdf5",
            color: "#047857",
            border: "1px solid #a7f3d0",
            fontWeight: 700,
            fontSize: "0.68rem",
            height: 22,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      ) : (
        <Chip label="Nou" size="small" sx={mutedChipSx} />
      )}
      {hasTel && (
        <Chip
          icon={<ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />}
          label=""
          size="small"
          sx={{
            bgcolor: "#ecfeff",
            color: "#0e7490",
            border: "1px solid #a5f3fc",
            fontWeight: 700,
            fontSize: "0.68rem",
            height: 22,
            width: 28,
            "& .MuiChip-icon": { color: "#0e7490" },
            "& .MuiChip-label": { display: "none" },
          }}
        />
      )}
    </Stack>
  );

  const renderStatusChip = (status) => {
    const normalized = (status || "").toLowerCase();
    const styles = {
      prospect: { label: "Prospect", bgcolor: "#fff7ed", color: "#c2410c" },
      acceptat: { label: "Acceptat", bgcolor: "#ecfdf5", color: "#047857" },
      transferat: { label: "Transferat", bgcolor: "#eef2ff", color: "#4338ca" },
      refuzat: { label: "Refuzat", bgcolor: "#fef2f2", color: "#b91c1c" },
    };
    const config = styles[normalized] || {
      label: "Creat",
      bgcolor: "#f8fafc",
      color: "#475569",
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          border: `1px solid ${alpha(config.color, 0.18)}`,
          fontWeight: 700,
          fontSize: "0.68rem",
          height: 22,
          maxWidth: 82,
          "& .MuiChip-label": {
            px: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
      />
    );
  };

  // 1. Fetch lista de produse
  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        "https://server-9p4hx.kinsta.app/api/vinted/get/products",
      );
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (res.ok && json.success) {
        const newProducts = json.data;
        setProducts((prev) => {
          // Doar dacă s-au modificat datele semnificativ (poți adapta logica)
          if (JSON.stringify(prev) !== JSON.stringify(newProducts)) {
            return newProducts;
          }
          return prev;
        });
      } else {
        throw new Error("Response JSON invalid");
      }
    } catch (err) {
      console.error("Eroare la fetch products:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // 2. La mount, apelăm fetchProducts
  useEffect(() => {
    fetchProducts();
  }, []);

  // 3. Handler la schimbarea tab-ului
  const handleChange = (event, newValue) => {
    setValue(newValue);

    // Resetează filtrele când părăsești tab-urile de produse
    if (newValue < 3) {
      resetProductFilters();
    }

    // Dacă intri în "Vânzători", fetch dacă nu există deja
    if (
      newValue === 3 &&
      sellers.length === 0 &&
      !sellersLoading &&
      !sellersError
    ) {
      fetchSellers();
    }

    // Reset când părăsești tab-ul "Vânzători"
    if (newValue !== 3) {
      setSelectedSellerId(null);
      setSellerProducts([]);
      setSellerProdError(null);
      setSellerProdLoading(false);
      setSellerAgentFilter("");
      setSelectedDetail(null);
      setDetailError(null);
      setDetailLoading(false);
    }
  };

  // 4. Filtrare produse pe tab-urile 0-2
  const getRowsForTab = (tabKey) => {
    let filtered = products;
    switch (tabKey) {
      case "Creat":
        filtered = filtered.filter(
          (item) => !item.status || item.status === "",
        );
        break;
      case "Prospect":
        filtered = filtered.filter(
          (item) => item.status && item.status.toLowerCase() === "prospect",
        );
        break;
      case "Acceptat":
        filtered = filtered.filter(
          (item) => item.status && item.status.toLowerCase() === "acceptat",
        );
        break;
      default:
        filtered = [];
    }

    if (brandFilter) {
      filtered = filtered.filter((item) => item.brand === brandFilter);
    }
    if (categoryFilter) {
      filtered = filtered.filter((item) => item.categorie === categoryFilter);
    }
    if (agentFilter) {
      filtered = filtered.filter((item) => item.agent === agentFilter);
    }
    if (modelFilter) {
      const q = modelFilter.toLowerCase();
      filtered = filtered.filter((p) =>
        (p.model || p.title || "").toLowerCase().includes(q),
      );
    }
    if (dateFrom || dateTo) {
      filtered = filtered.filter((item) => {
        const rawDate = item.product_data_contactat || item.data; // suport pentru ambele
        if (!rawDate) return false;
        const itemDate = new Date(rawDate);
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;

        if (from && itemDate < from) return false;
        if (to && itemDate > to) return false;

        return true;
      });
    }

    return filtered;
  };

  // Liste unice pentru filtre produse
  const allBrands = useMemo(() => {
    const setBr = new Set(products.map((p) => p.brand).filter((b) => b));
    return Array.from(setBr).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const allCategories = useMemo(() => {
    const setCat = new Set(products.map((p) => p.categorie).filter((c) => c));
    return Array.from(setCat).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const allAgents = useMemo(() => {
    const setAg = new Set(products.map((p) => p.agent).filter((a) => a));
    return Array.from(setAg).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filteredCounts = {
    Creat: getRowsForTab("Creat").length,
    Prospect: getRowsForTab("Prospect").length,
    Acceptat: getRowsForTab("Acceptat").length,
  };

  const productStats = useMemo(
    () => ({
      total: products.length,
      contacted: products.filter((p) => p.contactat === "Da").length,
      assigned: products.filter((p) => Boolean(p.agent)).length,
    }),
    [products],
  );

  const activeFiltersCount = [
    brandFilter,
    categoryFilter,
    agentFilter,
    modelFilter,
    dateFrom,
    dateTo,
  ].filter(Boolean).length;

  const tabsMeta = [
    {
      label: "Creat",
      count: filteredCounts.Creat,
      icon: <Inventory2Outlined fontSize="small" />,
    },
    {
      label: "Prospect",
      count: filteredCounts.Prospect,
      icon: <HourglassEmptyOutlined fontSize="small" />,
    },
    {
      label: "Acceptat",
      count: filteredCounts.Acceptat,
      icon: <CheckCircleOutline fontSize="small" />,
    },
    {
      label: "Vânzători",
      count: sellers.length,
      icon: <PeopleAltOutlined fontSize="small" />,
    },
  ];

  const summaryMetrics = [
    {
      label: "Produse active",
      value: productStats.total,
      caption: "vizibile în România",
      color: "#2563eb",
      icon: <Inventory2Outlined fontSize="small" />,
    },
    {
      label: "Contactate",
      value: productStats.contacted,
      caption: "marcate cu Da",
      color: "#059669",
      icon: <CheckCircleOutline fontSize="small" />,
    },
    {
      label: "Cu agent",
      value: productStats.assigned,
      caption: "alocate în lucru",
      color: "#7c3aed",
      icon: <PeopleAltOutlined fontSize="small" />,
    },
    {
      label: "Filtre",
      value: activeFiltersCount,
      caption: "active acum",
      color: "#ea580c",
      icon: <FilterAltOutlined fontSize="small" />,
    },
  ];

  // 5. Fetch lista de vânzători
  const fetchSellers = async () => {
    setSellersLoading(true);
    setSellersError(null);
    try {
      const res = await fetch(
        "https://server-9p4hx.kinsta.app/api/vinted/get/sellers",
      );
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setSellers(json.data);
      } else {
        throw new Error("Response JSON invalid");
      }
    } catch (err) {
      console.error("Eroare la fetch sellers:", err);
      setSellersError(err);
    } finally {
      setSellersLoading(false);
    }
  };

  // 6. Filtrare vânzători după agent
  const getFilteredSellers = () => {
    if (!sellerAgentFilter) return sellers;
    return sellers.filter((s) => s.agent === sellerAgentFilter);
  };

  // 7. Când se dă click pe un vânzător: fetch produse ale vânzătorului
  const onSellerClick = async (sellerId) => {
    setSelectedSellerId(sellerId);
    setSellerProducts([]);
    setSellerProdError(null);
    setSellerProdLoading(true);

    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/vinted/seller/${sellerId}/products`,
      );
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) {
        setSellerProducts(json.data);
      } else {
        throw new Error("Response JSON invalid");
      }
    } catch (err) {
      console.error("Eroare la fetch seller products:", err);
      setSellerProdError(err);
    } finally {
      setSellerProdLoading(false);
    }
  };

  // 8. Când se dă click pe un produs: fetch detaliu
  const onProductClick = (productId) => {
    setSelectedDetail(null);
    setDetailError(null);
    setDetailLoading(true);

    fetch(`https://server-9p4hx.kinsta.app/api/vinted/product/${productId}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (json.success) {
          setSelectedDetail(json.data);
        } else {
          throw new Error("Response JSON invalid");
        }
      })
      .catch((err) => {
        console.error("Eroare la fetch product detail:", err);
        setDetailError(err);
      })
      .finally(() => {
        setDetailLoading(false);
      });
  };

  // 9. Render tabel produse (tab-urile 0-2)
  const renderTable = (tabKey) => {
    const rows = getRowsForTab(tabKey);

    if (error) {
      return (
        <Box sx={{ ...surfaceSx, p: 2 }}>
          <Typography color="error" variant="subtitle2">
            Eroare la încărcarea produselor:
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {error.message}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          sx={{
            ...surfaceSx,
            p: 1.5,
          }}
        >
          <Stack
            direction={isSmDown ? "column" : "row"}
            alignItems={isSmDown ? "stretch" : "center"}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#eef2ff",
                  color: "#3730a3",
                }}
              >
                <FilterAltOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Filtre produse
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rows.length} rezultate în {tabKey.toLowerCase()}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {activeFiltersCount > 0 && (
                <Chip
                  label={`${activeFiltersCount} filtre active`}
                  size="small"
                  sx={mutedChipSx}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={resetProductFilters}
                sx={{ borderRadius: 1.5, textTransform: "none" }}
              >
                Reset
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<RefreshOutlined />}
                onClick={fetchProducts}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                Reîncarcă
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1.25,
            }}
          >
            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel id="brand-filter-label">Brand</InputLabel>
              <Select
                labelId="brand-filter-label"
                value={brandFilter}
                label="Brand"
                onChange={(e) => setBrandFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Toate</em>
                </MenuItem>
                {allBrands.map((brand) => (
                  <MenuItem key={brand} value={brand}>
                    {brand}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel id="category-filter-label">Categorie</InputLabel>
              <Select
                labelId="category-filter-label"
                value={categoryFilter}
                label="Categorie"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Toate</em>
                </MenuItem>
                {allCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth sx={fieldSx}>
              <InputLabel id="agent-filter-label">Agent</InputLabel>
              <Select
                labelId="agent-filter-label"
                value={agentFilter}
                label="Agent"
                onChange={(e) => setAgentFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>Toți</em>
                </MenuItem>
                {allAgents.map((ag) => (
                  <MenuItem key={ag} value={ag}>
                    {ag}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Caută model"
              value={modelFilter}
              onChange={(e) => setModelFilter(e.target.value)}
              fullWidth
              sx={fieldSx}
            />

            <TextField
              size="small"
              label="De la data"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={fieldSx}
            />

            <TextField
              size="small"
              label="Până la data"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={fieldSx}
            />
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          sx={tableContainerSx(
            isSmDown ? "calc(100vh - 390px)" : "calc(100vh - 360px)",
          )}
        >
          {loading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: alpha("#ffffff", 0.72),
                backdropFilter: "blur(2px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2,
              }}
            >
              <CircularProgress size={26} />
            </Box>
          )}
          <Table
            size="small"
            stickyHeader
            sx={{ width: "100%", minWidth: 0, tableLayout: "fixed" }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: "34%" }}>
                  Produs
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: "14%" }}>
                  Brand
                </TableCell>
                <TableCell sx={{ ...headerCellSx, width: "14%" }}>
                  Categorie
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, width: "12%", textAlign: "center" }}
                >
                  Agent
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, width: "12%", textAlign: "center" }}
                >
                  Contact
                </TableCell>
                <TableCell
                  sx={{ ...headerCellSx, width: "14%", textAlign: "right" }}
                >
                  Preț
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((item) => (
                  <TableRow
                    key={item.product_id}
                    hover
                    sx={{
                      ...tableRowSx,
                      bgcolor:
                        selectedDetail?.product_id === item.product_id
                          ? alpha(theme.palette.primary.main, 0.08)
                          : "inherit",
                    }}
                    onClick={() => onProductClick(item.product_id)}
                  >
                    <TableCell sx={{ ...bodyCellSx, maxWidth: 0 }}>
                      <Stack spacing={0.25}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.model || item.title || "-"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          ID {item.product_id}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 0,
                      }}
                    >
                      {item.brand}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 0,
                      }}
                    >
                      {item.categorie}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {renderAgentChip(item.agent)}
                    </TableCell>

                    <TableCell align="center" sx={bodyCellSx}>
                      {renderContactChips(item.contactat, Boolean(item.tel))}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.price}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Stack spacing={1} alignItems="center">
                      <SearchOutlined sx={{ color: "#94a3b8" }} />
                      <Typography variant="body2" color="text.secondary">
                        Nu există produse pentru criteriile date.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // 10. Render listă vânzători cu filtre+chip-uri
  const renderSellersList = () => {
    if (sellersLoading) {
      return (
        <Box
          sx={{
            ...surfaceSx,
            height: 240,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (sellersError) {
      return (
        <Box sx={{ ...surfaceSx, p: 2 }}>
          <Typography color="error" variant="subtitle2">
            Eroare la încărcarea vânzătorilor:
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {sellersError.message}
          </Typography>
        </Box>
      );
    }
    const filteredSellers = getFilteredSellers();
    const displayedSellers = [...filteredSellers].sort((a, b) =>
      sortDesc
        ? b.product_count - a.product_count
        : a.product_count - b.product_count,
    );

    if (filteredSellers.length === 0) {
      return (
        <Box sx={{ ...surfaceSx, p: 4, textAlign: "center" }}>
          <PeopleAltOutlined sx={{ color: "#94a3b8", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Nu există vânzători pentru filtrul selectat.
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box
          sx={{
            ...surfaceSx,
            p: 1.5,
          }}
        >
          <Stack
            direction={isSmDown ? "column" : "row"}
            alignItems={isSmDown ? "stretch" : "center"}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#ecfeff",
                  color: "#0e7490",
                }}
              >
                <StorefrontOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Vânzători
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {displayedSellers.length} vânzători sortați după produse
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={isSmDown ? "column" : "row"}
              alignItems={isSmDown ? "stretch" : "center"}
              spacing={1}
            >
              <FormControl
                size="small"
                sx={{ ...fieldSx, minWidth: isSmDown ? "100%" : 220 }}
              >
                <InputLabel id="seller-agent-filter-label">Agent</InputLabel>
                <Select
                  labelId="seller-agent-filter-label"
                  value={sellerAgentFilter}
                  label="Agent"
                  onChange={(e) => setSellerAgentFilter(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Toți</em>
                  </MenuItem>
                  {allAgents.map((ag) => (
                    <MenuItem key={ag} value={ag}>
                      {ag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                variant="contained"
                startIcon={<RefreshOutlined />}
                onClick={fetchSellers}
                sx={{
                  borderRadius: 1.5,
                  textTransform: "none",
                  boxShadow: "none",
                  minHeight: 40,
                }}
              >
                Reîncarcă
              </Button>
            </Stack>
          </Stack>
        </Box>

        <TableContainer
          component={Paper}
          sx={tableContainerSx(
            isSmDown ? "calc(100vh - 320px)" : "calc(100vh - 300px)",
          )}
        >
          <Table size="small" stickyHeader sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, minWidth: 220 }}>
                  Login
                </TableCell>
                <TableCell
                  sx={{
                    ...headerCellSx,
                    textAlign: "center",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => setSortDesc(!sortDesc)}
                >
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <SortOutlined sx={{ fontSize: 16 }} />
                    <span>Produse {sortDesc ? "↓" : "↑"}</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>
                  Contact
                </TableCell>
                <TableCell sx={{ ...headerCellSx, textAlign: "center" }}>
                  Agent
                </TableCell>
                <TableCell sx={{ ...headerCellSx }}>User ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedSellers.map((seller) => (
                <TableRow
                  key={seller.user_id}
                  hover
                  sx={tableRowSx}
                  onClick={() => onSellerClick(seller.user_id)}
                >
                  <TableCell
                    sx={{
                      ...bodyCellSx,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                      fontWeight: 700,
                    }}
                  >
                    {seller.login}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ ...bodyCellSx, fontWeight: 800 }}
                  >
                    {seller.product_count}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {renderContactChips(seller.contactat, false)}
                  </TableCell>
                  <TableCell align="center" sx={bodyCellSx}>
                    {renderAgentChip(seller.agent)}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, color: "text.secondary" }}>
                    {seller.user_id}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // 11. Render pentru lista de produse ale unui vânzător selectat
  const renderSellerProducts = () => {
    if (sellerProdLoading) {
      return (
        <Box
          sx={{
            ...surfaceSx,
            height: 240,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            ...surfaceSx,
            p: 1.5,
            display: "flex",
            alignItems: isSmDown ? "stretch" : "center",
            justifyContent: "space-between",
            flexDirection: isSmDown ? "column" : "row",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Produsele vânzătorului
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sellerProducts.length} produse în baza noastră
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackOutlined />}
            onClick={() => setSelectedSellerId(null)}
            sx={{ borderRadius: 1.5, textTransform: "none" }}
          >
            Înapoi la vânzători
          </Button>
        </Box>
        {sellerProdError ? (
          <Box sx={{ ...surfaceSx, p: 2 }}>
            <Typography color="error" variant="subtitle2">
              Eroare la încărcarea produselor pentru acest vânzător:
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {sellerProdError.message}
            </Typography>
          </Box>
        ) : sellerProducts.length === 0 ? (
          <Box sx={{ ...surfaceSx, p: 4, textAlign: "center" }}>
            <Inventory2Outlined sx={{ color: "#94a3b8", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Acest vânzător nu are produse în baza noastră.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={tableContainerSx(
              isSmDown ? "calc(100vh - 320px)" : "calc(100vh - 300px)",
            )}
          >
            <Table
              size="small"
              stickyHeader
              sx={{ width: "100%", minWidth: 0, tableLayout: "fixed" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: "30%" }}>
                    Produs
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: "13%" }}>
                    Brand
                  </TableCell>
                  <TableCell sx={{ ...headerCellSx, width: "13%" }}>
                    Categorie
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "11%", textAlign: "center" }}
                  >
                    Agent
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "11%", textAlign: "center" }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "12%", textAlign: "right" }}
                  >
                    Preț
                  </TableCell>
                  <TableCell
                    sx={{ ...headerCellSx, width: "10%", textAlign: "center" }}
                  >
                    Contactat
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sellerProducts.map((item) => (
                  <TableRow
                    key={item.product_id}
                    hover
                    sx={{
                      ...tableRowSx,
                      bgcolor:
                        selectedDetail?.product_id === item.product_id
                          ? alpha(theme.palette.primary.main, 0.08)
                          : "inherit",
                    }}
                    onClick={() => onProductClick(item.product_id)}
                  >
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 0,
                        fontWeight: 700,
                      }}
                    >
                      <Stack spacing={0.25}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item.model || item.title || "-"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          ID {item.product_id}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 0,
                      }}
                    >
                      {item.brand}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...bodyCellSx,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 0,
                      }}
                    >
                      {item.categorie}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {renderAgentChip(item.agent)}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {renderStatusChip(item.status)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...bodyCellSx,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.price}
                    </TableCell>
                    <TableCell align="center" sx={bodyCellSx}>
                      {renderContactChips(item.contactat, false)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  // 12. Render principal: taburi + conținut + panel detaliu
  return (
    <Box
      sx={{
        display: isSmDown ? "flex" : "grid",
        flexDirection: isSmDown ? "column" : "row",
        gridTemplateColumns: isSmDown
          ? undefined
          : "minmax(0, 1fr) minmax(360px, 44%)",
        width: "100%",
        height: "100vh",
        boxSizing: "border-box",
        bgcolor: "#f6f8fb",
        gap: 1.5,
        p: isSmDown ? 1 : 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: isSmDown ? "1 1 auto" : undefined,
          width: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          ...surfaceSx,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: isSmDown ? 1.25 : 1.75, pb: 1.25 }}>
          <Stack
            direction={isSmDown ? "column" : "row"}
            alignItems={isSmDown ? "stretch" : "center"}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#111827",
                  color: "#ffffff",
                }}
              >
                <StorefrontOutlined />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    lineHeight: 1.1,
                    color: "#0f172a",
                  }}
                >
                  Vinted
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pipeline produse și vânzători
                </Typography>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshOutlined />}
              onClick={() => {
                fetchProducts();
                if (value === 3) fetchSellers();
              }}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                alignSelf: isSmDown ? "flex-start" : "center",
              }}
            >
              Refresh date
            </Button>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1,
              mb: 1.5,
            }}
          >
            {summaryMetrics.map((metric) => (
              <Box
                key={metric.label}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 1.5,
                  p: 1.15,
                  bgcolor: "#fbfdff",
                  minWidth: 0,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.25,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(metric.color, 0.1),
                      color: metric.color,
                      flex: "0 0 auto",
                    }}
                  >
                    {metric.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", lineHeight: 1.1 }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 900, lineHeight: 1.1 }}
                    >
                      {metric.value}
                    </Typography>
                  </Box>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75 }}
                >
                  {metric.caption}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Vinted tabs"
          variant={isSmDown ? "scrollable" : "fullWidth"}
          scrollButtons="auto"
          sx={{
            px: isSmDown ? 1.25 : 1.75,
            minHeight: 44,
            borderBottom: "1px solid #e2e8f0",
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": {
              gap: 0.75,
              pb: 1.25,
            },
            "& .MuiTab-root": {
              minHeight: 38,
              borderRadius: 1.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
              color: "#475569",
              textTransform: "none",
              fontWeight: 800,
              px: 1.25,
            },
            "& .Mui-selected": {
              bgcolor: "#0f172a",
              color: "#ffffff !important",
              borderColor: "#0f172a",
            },
          }}
        >
          {tabsMeta.map((tab) => (
            <Tab
              key={tab.label}
              label={
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {tab.icon}
                  <span>{tab.label}</span>
                  <Chip
                    label={tab.count}
                    size="small"
                    sx={{
                      height: 20,
                      minWidth: 28,
                      bgcolor: "rgba(255,255,255,0.18)",
                      color: "inherit",
                      fontWeight: 800,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Stack>
              }
            />
          ))}
        </Tabs>

        <Box sx={{ flex: 1, overflow: "hidden", p: isSmDown ? 1 : 1.5 }}>
          {tabsMeta.map((tab, index) => (
            <div
              role="tabpanel"
              hidden={value !== index}
              key={tab.label}
              id={`tabpanel-${index}`}
              aria-labelledby={`tab-${index}`}
              style={{ height: "100%" }}
            >
              {value === index && (
                <Box sx={{ height: "100%" }}>
                  {index < 3 ? (
                    renderTable(tab.label)
                  ) : (
                    <>
                      {selectedSellerId
                        ? renderSellerProducts()
                        : renderSellersList()}
                    </>
                  )}
                </Box>
              )}
            </div>
          ))}
        </Box>
      </Box>

      {/* === DREAPTA: Panel detaliu produs === */}
      <Box
        sx={{
          flex: isSmDown ? "1 1 auto" : undefined,
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          height: isSmDown ? "55vh" : "100%",
          overflowX: "hidden",
          overflowY: "auto",
          boxSizing: "border-box",
          p: isSmDown ? 0 : 0.5,
          bgcolor: "#eef2f7",
          border: "1px solid #e2e8f0",
          borderRadius: 2,
        }}
      >
        <VintedProductDetails
          detail={selectedDetail}
          loading={detailLoading}
          error={detailError}
          onReload={() => {
            fetchProducts();
            if (selectedDetail && selectedDetail.product_id) {
              onProductClick(selectedDetail.product_id);
            }
          }}
          onStatusChange={() => {
            fetchProducts();
            setSelectedDetail(null);
          }}
        />
      </Box>
    </Box>
  );
}
